/**
 * Rewards API Client
 *
 * API functions for the rewards claim system.
 * Feature: 017-rewards-claim-system
 */

import { apiClient } from './client';
import { getCsrfToken } from './settings';

// ---- Types ----

export interface ClaimPeriod {
  id: string;
  project_id: string;
  status: 'open' | 'closed' | 'distributed';
  opened_at: string;
  closed_at: string | null;
  distributed_at: string | null;
  eligible_collection_ids: string[];
  required_reward_address_type: string;
  min_points_threshold: number;
  custom_message: string | null;
  rate_mode: 'per_point' | 'fixed_pool';
  rate_per_point: number | null;
  total_pool: number | null;
  distribution_mode: string;
  total_points_all_holders: number | null;
  total_points_claimants: number | null;
  total_claims: number | null;
  total_eligible_wallets: number | null;
  live_claim_count?: number;
  created_at: string;
  updated_at: string;
}

export interface RewardClaim {
  id: string;
  period_id: string;
  user_id: string;
  claimed_at: string;
  xrpl_address: string;
  reward_address: string | null;
  snapshotted_points: number | null;
  snapshotted_points_breakdown: Record<string, {
    name: string;
    points: number;
    nft_count: number;
  }> | null;
  calculated_payout: number | null;
  rank: number | null;
  live_points?: number;
  created_at: string;
  updated_at: string;
}

export interface EligibleCollection {
  id: string;
  name: string;
}

export interface ActivePeriodResponse {
  period: ClaimPeriod | null;
  userClaims: RewardClaim[];
  eligible_collections?: EligibleCollection[];
}

export interface ClaimHistoryEntry extends RewardClaim {
  project_name: string;
  project_slug: string;
  period_status: string;
  period_opened_at: string;
  period_closed_at: string | null;
}

// ---- Public / Holder API ----

export async function getActivePeriod(projectSlug: string): Promise<ActivePeriodResponse> {
  return apiClient.get<ActivePeriodResponse>(`/projects/${projectSlug}/rewards/periods/active`);
}

export async function submitClaim(projectSlug: string): Promise<{ claims: RewardClaim[]; claimedCount: number }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ claims: RewardClaim[]; claimedCount: number }>(`/projects/${projectSlug}/rewards/claim`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({}),
  });
}

export async function getMyClaim(projectSlug: string): Promise<{ period: ClaimPeriod | null; claims: RewardClaim[] }> {
  return apiClient.get<{ period: ClaimPeriod | null; claims: RewardClaim[] }>(`/projects/${projectSlug}/rewards/my-claim`);
}

export async function getMyClaimHistory(options?: { limit?: number; offset?: number }): Promise<{ claims: ClaimHistoryEntry[] }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get<{ claims: ClaimHistoryEntry[] }>(`/rewards/my-claims${qs ? `?${qs}` : ''}`);
}

export async function getPublicPeriods(projectSlug: string): Promise<{ periods: ClaimPeriod[] }> {
  return apiClient.get<{ periods: ClaimPeriod[] }>(`/projects/${projectSlug}/rewards/periods/public`);
}

// ---- Owner API ----

export interface OpenPeriodConfig {
  eligible_collection_ids: string[];
  required_reward_address_type: string;
  min_points_threshold?: number;
  rate_mode?: string;
  rate_per_point?: number;
  total_pool?: number;
  custom_message?: string;
}

export async function openPeriod(projectSlug: string, config: OpenPeriodConfig): Promise<{ period: ClaimPeriod }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ period: ClaimPeriod }>(`/projects/${projectSlug}/rewards/periods`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(config),
  });
}

export async function getPeriods(projectSlug: string): Promise<{ periods: ClaimPeriod[] }> {
  return apiClient.get<{ periods: ClaimPeriod[] }>(`/projects/${projectSlug}/rewards/periods`);
}

export async function closePeriod(projectSlug: string, periodId: string): Promise<{ period: ClaimPeriod }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ period: ClaimPeriod }>(`/projects/${projectSlug}/rewards/periods/${periodId}/close`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({}),
  });
}

export async function setRate(
  projectSlug: string,
  periodId: string,
  data: { rate_mode: string; rate_per_point?: number; total_pool?: number }
): Promise<{ period: ClaimPeriod }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ period: ClaimPeriod }>(`/projects/${projectSlug}/rewards/periods/${periodId}/rate`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify(data),
  });
}

export async function markDistributed(projectSlug: string, periodId: string): Promise<{ period: ClaimPeriod }> {
  const csrfToken = await getCsrfToken();
  return apiClient.request<{ period: ClaimPeriod }>(`/projects/${projectSlug}/rewards/periods/${periodId}/distribute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({}),
  });
}

export interface ClaimantsResponse {
  claims: RewardClaim[];
  total: number;
  period?: {
    rate_mode: string;
    rate_per_point: number | null;
    total_pool: number | null;
    required_reward_address_type: string;
    total_eligible_wallets: number | null;
    total_points_all_holders: number | null;
    total_points_claimants: number | null;
  };
  eligible_stats?: {
    total_wallets: number;
    total_points: number;
  };
}

export async function getClaimants(
  projectSlug: string,
  periodId: string,
  options?: { limit?: number; offset?: number }
): Promise<ClaimantsResponse> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get<ClaimantsResponse>(
    `/projects/${projectSlug}/rewards/periods/${periodId}/claims${qs ? `?${qs}` : ''}`
  );
}

export function getExportUrl(projectSlug: string, periodId: string): string {
  const baseUrl = (apiClient as any)['baseUrl'] || '/api/v1';
  return `${baseUrl}/projects/${projectSlug}/rewards/periods/${periodId}/export`;
}

export async function exportCSV(projectSlug: string, periodId: string): Promise<void> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const url = getExportUrl(projectSlug, periodId);

  const response = await fetch(url, {
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download CSV');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = `rewards-${projectSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(downloadUrl);
}
